<template>
  <div class="settings-menu" :class="{ ['hide']: !submenuOpened }">
    <div
      class="settings-menu__wrapper"
      :class="{ ['settings-menu__mobile-opened']: this.mobileMenuOpened }"
    >
      <h3>Settings</h3>

      <ul class="settings-menu__list">
        <li
          v-for="link in links"
          :key="link.url"
          :class="{
            ['active']:
              link.active || _submenuStatusCheck(link.submenu, link.key),
            ['list-item']: true
          }"
          @click="e => getLinkAction(e, link)"
        >
          <div class="settings-menu__item">
            <div v-if="link.hasAlert" class="settings-menu__alert">
              <img :src="alertSvg" alt="alert" />
            </div>

            <div class="item-text">
              <span> {{ link.label }} </span>

              <i
                v-if="link.submenu"
                :class="
                  `far fa-chevron-${
                    submenuOpenedId === link.key ? 'up' : 'down'
                  }`
                "
              ></i>
            </div>

            <ul
              class="settings-menu__submenu"
              v-if="_submenuStatusCheck(link.submenu, link.key)"
            >
              <li
                v-for="subLink in link.submenu"
                class="submenu-list-item"
                :class="{ ['active']: subLink.active }"
                @click="e => getLinkAction(e, subLink)"
              >
                <span> {{ subLink.label }} </span>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>

    <VConfirmModal
      :isOpened="logoutConfirmOpened"
      :titles="{ primary: 'Sign Out', secondary: 'Cancel' }"
      @approve="onDeleteApprove"
      @cancel="logoutConfirmOpened = false"
    >
      <div class="confirm-text">
        You will be signed out and redirected <br />
        to the Login Page.
      </div>
    </VConfirmModal>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import withNavigate from "@/mixins/withNavigate";
import VConfirmModal from "@/components/VConfirmModal";
import alertSvg from "@/assets/images/alert.svg";
import userLogo from "@/assets/images/default-user.png";
import { mapGetters } from "vuex";

export default Vue.component("TheSettingsMenu", {
  props: {
    avatar: VueTypes.string,
    links: VueTypes.arrayOf(
      VueTypes.shape({
        key: VueTypes.string.isRequired,
        url: VueTypes.string.isRequired,
        label: VueTypes.string.isRequired,
        active: VueTypes.bool.def(false),
        external: VueTypes.bool,
        submenu: VueTypes.array
      })
    )
  },

  components: {
    VConfirmModal
  },

  mixins: [withNavigate],

  data() {
    return {
      mobileMenuOpened: false,
      logoutConfirmOpened: false,
      componentLoaded: false,
      submenuOpenedId: this._initialSubmenuStatus,
      alertSvg,
      userLogo
    };
  },

  computed: {
    _initialSubmenuStatus() {
      return this.links
        .filter(link => link.submenu)
        .some(link => this.hasActiveSubmenu(link.submenu));
    },

    ...mapGetters("service", ["submenuOpened"])
  },

  methods: {
    _submenuStatusCheck(submenu, parentKey) {
      if (!submenu) {
        return;
      }

      const currentActive = this.submenuOpenedId === parentKey;

      if (!currentActive && this.componentLoaded) {
        return false;
      }

      return currentActive ? true : this.hasActiveSubmenu(submenu);
    },

    toggleMobileMenu() {
      this.mobileMenuOpened = !this.mobileMenuOpened;
    },

    getLinkAction(e, link) {
      e.stopPropagation();

      if (link.submenu) {
        if (this.hasActiveSubmenu(link.submenu)) {
          return;
        }

        this.submenuOpenedId =
          this.submenuOpenedId === link.key ? null : link.key;

        return;
      } else {
        this.submenuOpenedId = null;
      }

      if (link.external) {
        return this.externalBlank(link.url);
      }

      if (link.key === "logout") {
        return (this.logoutConfirmOpened = true);
      }

      return this.navigate(link.url);
    },

    onDeleteApprove() {
      this.logoutConfirmOpened = false;
      this.navigate("/logout");
    },

    hasActiveSubmenu(linksArray) {
      return linksArray.some(item => item.active);
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "settings-menu";

.#{$ns} {
  position: relative;

  &.hide {
    .#{$ns}__wrapper {
      width: 0;
      opacity: 0;

      .#{$ns}__avatar {
        visibility: hidden;
      }
    }
  }
}

.#{$ns}__wrapper {
  display: flex;
  flex-direction: column;
  background: transparent;
  width: $settings-menu-width;
  padding: $general-padding 0 20px;
  height: 100%;
  transition: all 0.1s cubic-bezier(0.165, 0.84, 0.44, 1);
  opacity: 1;

  h3 {
    font-size: 22px;
    text-align: left;
    padding-left: 30px;
    min-height: $input-height;
    display: flex;
    align-items: center;
    color: $primary-text-color;
  }

  .#{$ns}__list {
    text-decoration: none;
    color: $input-label-primary-color;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    margin-top: 35px;

    li.list-item {
      padding: 0 30px;
      font-family: "Lato-Regular", sans-serif;
      color: $primary-text-color;

      .item-text {
        min-height: 26px;
        display: flex;
        align-items: center;
      }

      &,
      .submenu-list-item {
        display: flex;
        cursor: pointer;
        font-size: 15px;
        transition: color 0.15s linear;
        align-items: center;
        margin-bottom: 25px;
        min-height: 26px;

        i {
          color: $secondary-text-color;
          margin-left: 11px;
        }

        &.active,
        &:hover {
          color: $primary-color;
          /*font-weight: 600;*/

          i {
            color: $primary-color;
          }
        }
      }

      .submenu-list-item {
        padding-left: 10px;
        span {
          font-family: Lato-Light, sans-serif;
          font-weight: 300;
        }
      }

      .#{$ns}__item {
        position: relative;
        display: inline-block;

        .#{$ns}__alert {
          position: absolute;
          left: -25px;
          width: 15px;

          img {
            width: 100%;
            height: 100%;
          }
        }

        i {
        }
      }

      &:last-child {
        position: absolute;
        bottom: 20px;
        font-family: Lato-Regular, sans-serif;
        color: $primary-text-color;
        height: 34px;
        margin-bottom: 0;

        &:hover {
          color: $primary-color;
        }
      }

      .#{$ns}__submenu {
        li {
          font-family: "Lato-Light", sans-serif;
          color: $secondary-text-color;

          &:first-child {
            margin-top: 25px;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }
}


@media screen and (max-width: 991px) {
  .#{$ns}__wrapper {
    position: fixed;
    height: 100%;
    left: -100vw;
    transition: left 0.3s ease-in-out;
    padding: 70px 0;
    background: #f5f7f9;

    &.#{$ns}__mobile-opened {
      left: 0;
      z-index: 1;
    }
  }
}
</style>

<style lang="scss">
button.sm.settings-menu-mobile i {
  font-size: 16px;
}

div.confirm-text {
  color: $secondary-border-color;

  span {
    color: $primary-color;
  }
}
</style>
